import { Organization } from '../../../../domain/organization/dto/Organization';
import { Profile } from '../../../../domain/cabinet/dto/Profile';
import { FmsRole, PortalRole } from '../../../../domain/auth/service/auth.service';

export class InitDataDTO {
  organizations: OrganizationWithAccesses[];
  userAccess: InitDataUserAccess;
  profile: Profile;


  constructor(organizations: OrganizationWithAccesses[], userAccess: InitDataUserAccess, profile: Profile) {
    this.organizations = organizations;
    this.userAccess = userAccess;
    this.profile = profile;
  }

  static deserialize(object: InitDataDTO): InitDataDTO {
    return new InitDataDTO(
      object.organizations.map((organization) => OrganizationWithAccesses.deserialize(organization)),
      object.userAccess,
      object.profile,
    );
  }
}

export class OrganizationWithAccesses {
  organization: Organization;
  userAccesses: UserAccessesInsideOrgDto;

  constructor(organization: Organization, userAccesses: UserAccessesInsideOrgDto) {
    this.organization = organization;
    this.userAccesses = userAccesses;
  }

  static deserialize(object: OrganizationWithAccesses): OrganizationWithAccesses {
    return new OrganizationWithAccesses(
      Organization.deserialize(object.organization),
      UserAccessesInsideOrgDto.deserialize(object.userAccesses),
    );
  }
}

export enum EmployeeRole {
  noAccess='noAccess',
  viewer='viewer',
  admin='admin',
  externalAdminForOrgWithoutAccess = 'externalAdminForOrgWithoutAccess',
  externalAdminForOrgWithTrialExpired = 'externalAdminForOrgWithTrialExpired',
  externalAdmin='externalAdmin',
  trialAccessExpired = 'trialAccessExpired',
  externalViewer='externalViewer',
}

export class UserAccessesInsideOrgDto {
  fmsAccess: EmployeeRole;
  kpiAccess: EmployeeRole;
  marketAnalysisAccess: EmployeeRole;
  portalAccess: EmployeeRole;
  dataRoomAccess: EmployeeRole;
  financialStatementAccess: EmployeeRole;
  pdfAccess: EmployeeRole;
  isOrgAdmin: boolean;

  static deserialize(object: UserAccessesInsideOrgDto): UserAccessesInsideOrgDto {
    return Object.assign(new UserAccessesInsideOrgDto(), object);
  }

  isFmsAdmin(): boolean {
    return this.fmsAccess.toString().toLowerCase().includes('admin');
  }

  isKpiAdmin(): boolean {
    return this.kpiAccess.toString().toLowerCase().includes('admin');
  }

  isPortalAdmin(): boolean {
    return this.portalAccess.toString().toLowerCase().includes('admin');
  }

  isPDFAdmin(): boolean {
    return this.pdfAccess.toString().toLowerCase().includes('admin');
  }

  isFsAdmin(): boolean {
    return this.financialStatementAccess.toString().toLowerCase().includes('admin');
  }

  isDataRoomAdmin(): boolean {
    return this.dataRoomAccess.toString().toLowerCase().includes('admin');
  }

  isAdminSomewhere(): boolean {
    return this.isFmsAdmin() || this.isPortalAdmin() || this.isPDFAdmin() || this.isFsAdmin() || this.isKpiAdmin() ||
      this.isDataRoomAdmin();
  }

  /**
   * Checks if the user has at least one type of access.
   * If the user has only Market Analysis access, this method
   * verifies if the access is granted in the current organization.
   * This is necessary because Market Analysis access might be
   * inherited from another organization.
   *
   * @param selectedOrgUserAccesses - The access information of the user for the selected organization.
   * @returns - True if the user has at least one access, otherwise false.
   */
  hasAtLeastOneAccess(selectedOrgUserAccesses: OrganizationWithAccesses): boolean {
    let access = this.fmsAccess !== EmployeeRole.noAccess ||
           this.portalAccess !== EmployeeRole.noAccess ||
           this.kpiAccess !== EmployeeRole.noAccess ||
           this.pdfAccess !== EmployeeRole.noAccess ||
           this.financialStatementAccess !== EmployeeRole.noAccess ||
           this.dataRoomAccess !== EmployeeRole.noAccess ||
           this.marketAnalysisAccess !== EmployeeRole.noAccess;
    if (this.isOnlyMaAccess()) {
      access = selectedOrgUserAccesses.userAccesses.marketAnalysisAccess === EmployeeRole.viewer;
    }
    return access;
  }

  private isOnlyMaAccess(): boolean {
    return this.fmsAccess === EmployeeRole.noAccess &&
      this.portalAccess === EmployeeRole.noAccess &&
      this.kpiAccess === EmployeeRole.noAccess &&
      this.pdfAccess === EmployeeRole.noAccess &&
      this.financialStatementAccess === EmployeeRole.noAccess &&
      this.dataRoomAccess !== EmployeeRole.noAccess &&
      this.marketAnalysisAccess !== EmployeeRole.noAccess;
  }
}

export interface InitDataUserAccess{
  userRole: FmsRole;
  portalRole: PortalRole;
}
